// Caminho: vita-frontend/src/pages/Client/ClientSearchPage.js

import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { getAllProjects } from "../../services/projectService";
import { getDisplayName } from "../../utils/usersUtils";
import { useAuth } from "../../contexts/AuthContext";
import ProjectNotificationsPanel from "../../components/layout/ProjectNotificationsPanel";
import { FaBell, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import Pagination from "../../components/layout/Pagination";

// --- Styled Components (reutilizados) ---
const SearchContainer = styled.div`
    padding: 24px;
`;

const Title = styled.h1`
    color: ${({ theme }) => theme.colors.primary};
    margin-bottom: 24px;
`;

const Controls = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 24px;
`;

const SearchInput = styled.input`
    flex-grow: 1;
    min-width: 250px;
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 4px;
`;

const TableWrapper = styled.div`
    background-color: white;
    border-radius: 8px;
    padding: 24px;
    overflow-x: auto;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const StyledTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    th,
    td {
        padding: 12px 15px;
        text-align: left;
        border-bottom: 1px solid #f1f1f1;
    }
    th {
        font-weight: bold;
        color: #800020;
    }
    tbody tr {
        cursor: pointer;
        &:hover {
            background-color: #f9f9f9;
        }
    }
`;

const StatusBadge = styled.span`
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: bold;
    color: white;
    background-color: ${({ status }) => {
        switch (status) {
            case "Em andamento":
                return "#6c757d";
            case "Em atraso":
                return "#ffc107";
            case "Finalizado":
                return "#28a745";
            default:
                return "#6c757d";
        }
    }};
`;

const FilterContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
    padding: 10px;
    background-color: #f8f9fa;
    border-radius: 4px;
`;

const FilterLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 5px;
    cursor: pointer;
`;

const ThSortable = styled.th`
    cursor: pointer;
    &:hover {
        background-color: #f1f1f1;
    }
`;

// Componente auxiliar para renderizar o ícone de ordenação correto
const SortIcon = ({ columnKey, sortConfig }) => {
    if (sortConfig.key !== columnKey) {
        return <FaSort size={12} style={{ marginLeft: "5px", opacity: 0.4 }} />;
    }
    if (sortConfig.direction === "ascending") {
        return <FaSortUp size={12} style={{ marginLeft: "5px" }} />;
    }
    return <FaSortDown size={12} style={{ marginLeft: "5px" }} />;
};

// --- Helpers ---
const stripRef = (ref) => {
    if (!ref || typeof ref !== "string") return null;
    return ref.includes("/") ? ref.split("/").pop() : ref;
};

// --- Componente da Página ---
const ClientSearchPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth(); // Pega o cliente logado
    const [projects, setProjects] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilters, setStatusFilters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [error, setError] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({
        key: "startDate",
        direction: "descending",
    });
    const itemsPerPage = 10;

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, statusFilters]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const projectsData = await getAllProjects();
                setProjects(projectsData.projects || []);
            } catch (err) {
                setError("Não foi possível carregar os projetos.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);


    const handleStatusFilterChange = (e) => {
        const { value, checked } = e.target;
        setStatusFilters((prev) =>
            checked
                ? [...prev, value]
                : prev.filter((status) => status !== value)
        );
    };

    const handleSort = (key) => {
        let direction = "ascending";
        if (sortConfig.key === key && sortConfig.direction === "ascending") {
            direction = "descending";
        }
        setSortConfig({ key, direction });
        setCurrentPage(1);
    };

    const filteredProjects = useMemo(() => {
        if (!Array.isArray(projects) || projects.length === 0) {
            return [];
        }

        let filtered = projects.filter((item) => {
            const  project  = item;
            const client = item.client;
            const employee = item.employee;
            if (
                statusFilters.length > 0 &&
                !statusFilters.includes(project.status)
            ) {
                return false;
            }
            const clientName = client ? getDisplayName(client) : "";
            const employeeName = employee
                ? getDisplayName(employee)
                : "";
            const searchTermLower = searchTerm.toLowerCase();

            return (
                searchTermLower === "" ||
                project.name.toLowerCase().includes(searchTermLower) ||
                project.id.toString().includes(searchTermLower) ||
                (clientName &&
                    clientName.toLowerCase().includes(searchTermLower)) ||
                (employeeName &&
                    employeeName.toLowerCase().includes(searchTermLower))
            );
        });

        // 2. Ordenação
        if (sortConfig.key) {
            filtered.sort((a, b) => {
                let aValue, bValue;

                // Define os valores a serem comparados baseado na chave (key)
                switch (sortConfig.key) {
                    case "name":
                        aValue = a.name?.toLowerCase() || "";
                        bValue = b.name?.toLowerCase() || "";
                        break;
                    case "clientName":
                        aValue = a.client
                            ? getDisplayName(a.client)?.toLowerCase()
                            : "";
                        bValue = b.client
                            ? getDisplayName(b.client)?.toLowerCase()
                            : "";
                        break;
                    case "employeeName":
                        aValue = a.employee
                            ? getDisplayName(
                                  a.employee
                              )?.toLowerCase()
                            : "";
                        bValue = b.employee
                            ? getDisplayName(
                                  b.employee
                              )?.toLowerCase()
                            : "";
                        break;
                    case "startDate": // Assumindo que startDate é uma string ISO válida
                        aValue = a.startDate
                            ? new Date(a.startDate)
                            : new Date(0); // Data muito antiga se nula
                        bValue = b.startDate
                            ? new Date(b.startDate)
                            : new Date(0);
                        break;
                    default: // Ordena por ID por padrão se a chave for inválida
                        aValue = a.id;
                        bValue = b.id;
                }

                // Lógica de comparação
                if (aValue < bValue) {
                    return sortConfig.direction === "ascending" ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === "ascending" ? 1 : -1;
                }
                return 0; // Se forem iguais
            });
        }

        return filtered;
        // Adicione sortConfig às dependências do useMemo
    }, [projects, searchTerm, statusFilters, sortConfig]);

    // O useMemo de currentProjects não precisa mudar, ele já opera sobre filteredProjects

    const currentProjects = useMemo(() => {
        const indexOfLastItem = currentPage * itemsPerPage;
        const indexOfFirstItem = indexOfLastItem - itemsPerPage;
        return filteredProjects.slice(indexOfFirstItem, indexOfLastItem);
    }, [filteredProjects, currentPage, itemsPerPage]);

    const handleNotificationClick = (project, e) => {
        e.stopPropagation();
        setSelectedProject(project); // Passa o objeto do projeto para o painel
        setIsPanelOpen(true);
    };

    if (loading) return <div>Carregando...</div>;
    if (error) return <div style={{ color: "red" }}>{error}</div>;

    return (
        <SearchContainer>
            <Title>Seus Projetos</Title>
            <Controls>
                <SearchInput
                    type="text"
                    placeholder="Pesquisar em seus projetos por nome do projeto"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </Controls>
            <FilterContainer>
                <strong>Filtrar por Status:</strong>
                <FilterLabel>
                    <input
                        type="checkbox"
                        value="Em andamento"
                        onChange={handleStatusFilterChange}
                    />
                    Em andamento
                </FilterLabel>
                <FilterLabel>
                    <input
                        type="checkbox"
                        value="Em atraso"
                        onChange={handleStatusFilterChange}
                    />
                    Em atraso
                </FilterLabel>
                <FilterLabel>
                    <input
                        type="checkbox"
                        value="Finalizado"
                        onChange={handleStatusFilterChange}
                    />
                    Finalizado
                </FilterLabel>
            </FilterContainer>
            <br />
            <TableWrapper>
                <StyledTable>
                    <thead>
                        <tr>
                            <ThSortable onClick={() => handleSort("name")}>
                                Nome do Projeto{" "}
                                <SortIcon
                                    columnKey="name"
                                    sortConfig={sortConfig}
                                />
                            </ThSortable>
                            <ThSortable
                                onClick={() => handleSort("employeeName")}
                            >
                                Responsável{" "}
                                <SortIcon
                                    columnKey="employeeName"
                                    sortConfig={sortConfig}
                                />
                            </ThSortable>
                            {/* Adicionando ordenação por data */}
                            <ThSortable onClick={() => handleSort("startDate")}>
                                Data de Início{" "}
                                <SortIcon
                                    columnKey="startDate"
                                    sortConfig={sortConfig}
                                />
                            </ThSortable>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentProjects.map((item) => (
                            <tr
                                key={item.id}
                                onClick={() =>
                                    navigate(
                                        `/client/projeto/${item.id}`
                                    )
                                }
                            >
                                <td>{item.name}</td>
                                <td>
                                    {item.employee
                                        ? getDisplayName(
                                              item.employee
                                          )
                                        : "N/A"}
                                </td>
                                <td>
                                    {new Date(
                                        item.startDate
                                    ).toLocaleDateString()}
                                </td>
                                <td>
                                    <StatusBadge status={item.status}>
                                        {item.status}
                                    </StatusBadge>
                                </td>
                                <td
                                    onClick={(e) =>
                                        handleNotificationClick(item, e)
                                    }
                                >
                                    <FaBell
                                        style={{
                                            cursor: "pointer",
                                            fontSize: "18px",
                                        }}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </StyledTable>
            </TableWrapper>
            <Pagination
                totalItems={filteredProjects.length}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
            />
            {selectedProject && (
                <ProjectNotificationsPanel
                    isOpen={isPanelOpen}
                    onClose={() => setIsPanelOpen(false)}
                    projectId={selectedProject.id}
                    projectName={selectedProject.name}
                />
            )}
        </SearchContainer>
    );
};

export default ClientSearchPage;
